import React from "react";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardMedia from "@mui/material/CardMedia";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import { Box } from "@mui/material";

const BlogComponent = ({ cardData }) => {
  const [focusedCardIndex, setFocusedCardIndex] = React.useState(null);

  const handleFocus = (index) => {
    setFocusedCardIndex(index);
  };

  const handleBlur = () => {
    setFocusedCardIndex(null);
  };

  const getFirstSentence = (html) => {
    if (!html) return ""; // Prevent errors if content is undefined/null

    // Create a temporary div to parse HTML
    const div = document.createElement("div");
    div.innerHTML = html;

    // Extract text content from HTML
    const text = div.textContent || div.innerText || "";

    // Use regex to find the first sentence (ends with ., !, or ?)
    const match = text.match(/[^.!?]+[.!?]/);
    
    return match ? match[0] : text; // Return first sentence or full text if no match
  };

  return (
    <div>
      <SyledCard
        variant="outlined"
        onFocus={() => handleFocus(1)}
        onBlur={handleBlur}
        tabIndex={0}
        className={focusedCardIndex === 1 ? "Mui-focused" : ""}
        sx={{ height: "100%" }}
      >
        <CardMedia
          component="img"
          alt="blog image post"
          image={cardData.image_url}
          sx={{
            height: { sm: "auto", md: "50%" },
            aspectRatio: { sm: "16 / 9", md: "" },
          }}
        />
        <SyledCardContent>
          <Typography gutterBottom variant="caption" component="div">
            {cardData?.tag}
          </Typography>
          <Typography gutterBottom variant="h6" component="div">
            {cardData.title}
          </Typography>
          <StyledTypography variant="body2" color="text.secondary" gutterBottom>
          <div dangerouslySetInnerHTML={{ __html: getFirstSentence(cardData.blog_post) }}></div>
          </StyledTypography>
        </SyledCardContent>
        <Author author={cardData.user} createdAt={cardData.created_at}/>
      </SyledCard>
    </div>
  );
};

export default BlogComponent;

const SyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: 0,
  height: "100%",
  backgroundColor: (theme.vars || theme).palette.background.paper,
  "&:hover": {
    backgroundColor: "transparent",
    cursor: "pointer",
  },
  "&:focus-visible": {
    outline: "3px solid",
    outlineColor: "hsla(210, 98%, 48%, 0.5)",
    outlineOffset: "2px",
  },
}));

const SyledCardContent = styled(CardContent)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: 16,
  flexGrow: 1,
  "&:last-child": {
    paddingBottom: 16,
  },
});

const StyledTypography = styled(Typography)({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

function Author({ author, createdAt }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          alignItems: "center",
        }}
      >
        <AvatarGroup max={3}>
          <Avatar
            key={author.id}
            alt={author.name}
            src={author.avatar}
            sx={{ width: 24, height: 24 }}
          />
        </AvatarGroup>
        <Typography variant="caption">{author.name}</Typography>
      </Box>
      <Typography variant="caption">{createdAt}</Typography>
    </Box>
  );
}
